import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Credential } from '../types';
import { Key, FileText, TrendingUp, AlertCircle, Clock, Plus, ShieldCheck, ArrowUpRight } from 'lucide-react';
import './Dashboard.css';

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();

  const { data: credentialsData, isLoading: credentialsLoading } = useQuery('credentials', async () => {
    const response = await api.get('/credentials');
    return response.data;
  });

  const { data: auditData, isLoading: auditLoading } = useQuery('audit-recent', async () => {
    const response = await api.get('/audit?limit=10');
    return response.data;
  });

  const credentials: Credential[] = credentialsData?.credentials || [];
  const recentLogs = auditData?.logs || [];
  const isLoading = credentialsLoading || auditLoading;

  // Calculate expiration stats
  const now = new Date();
  const credentialsWithExpiry = credentials.filter((c) => c.expiresAt);
  const credentialsWithoutExpiry = credentials.length - credentialsWithExpiry.length;

  const expiringCritical = credentials.filter((c) => {
    if (!c.expiresAt) return false;
    const expires = new Date(c.expiresAt);
    const daysUntilExpiry = (expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry > 0 && daysUntilExpiry <= 7; // Next 7 days
  }).length;

  const expiringSoon = credentials.filter((c) => {
    if (!c.expiresAt) return false;
    const expires = new Date(c.expiresAt);
    const daysUntilExpiry = (expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry > 7 && daysUntilExpiry <= 30; // 8-30 days
  }).length;

  const expired = credentials.filter((c) => {
    if (!c.expiresAt) return false;
    const expires = new Date(c.expiresAt);
    return expires.getTime() < now.getTime();
  }).length;

  const healthy = credentials.length - expiringCritical - expiringSoon - expired;

  const stats = {
    totalCredentials: credentials.length,
    expiringCritical, // 0-7 days
    expiringSoon, // 8-30 days
    expired,
    credentialsWithoutExpiry,
    recentActivity: recentLogs.length,
  };

  // Simple donut chart segments (pure SVG, no charting dependency)
  const donutTotal = Math.max(credentials.length, 1);
  const segments = [
    { label: 'Healthy', value: healthy, color: 'var(--color-success-500)' },
    { label: 'Expiring soon', value: expiringSoon, color: 'var(--color-warning-500)' },
    { label: 'Critical', value: expiringCritical, color: '#f97316' },
    { label: 'Expired', value: expired, color: 'var(--color-danger-500)' },
  ].filter((s) => s.value > 0);

  const circumference = 2 * Math.PI * 42;
  let offsetAcc = 0;

  if (isLoading) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>{greeting()}{user?.username ? `, ${user.username}` : ''}</h1>
          <p>Overview of your password management system</p>
        </div>
        <div className="stats-grid">
          {[0, 1, 2, 3].map((i) => (
            <div className="stat-card" key={i}>
              <div className="skeleton" style={{ width: 56, height: 56, borderRadius: 'var(--radius-lg)' }} />
              <div className="stat-content">
                <div className="skeleton" style={{ width: '60%', height: 28, marginBottom: 8 }} />
                <div className="skeleton" style={{ width: '80%', height: 14 }} />
              </div>
            </div>
          ))}
        </div>
        <div className="dashboard-grid">
          <div className="dashboard-card">
            <div className="skeleton" style={{ width: '100%', height: 220 }} />
          </div>
          <div className="dashboard-card">
            <div className="skeleton" style={{ width: '100%', height: 220 }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>{greeting()}{user?.username ? `, ${user.username}` : ''}</h1>
        <p>Overview of your password management system</p>
      </div>

      <div className="stats-grid">
        <Link to="/credentials" className="stat-card stat-card-link">
          <div className="stat-icon" style={{ backgroundColor: 'var(--color-primary-500)' }}>
            <Key size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalCredentials}</div>
            <div className="stat-label">Total Credentials</div>
            {stats.credentialsWithoutExpiry > 0 && (
              <div className="stat-hint">{stats.credentialsWithoutExpiry} without expiration date</div>
            )}
          </div>
          <ArrowUpRight size={16} className="stat-card-arrow" />
        </Link>

        <div className={`stat-card ${stats.expiringCritical > 0 ? 'stat-card-warning' : ''}`}>
          <div className="stat-icon" style={{ backgroundColor: stats.expiringCritical > 0 ? 'var(--color-danger-500)' : 'var(--color-warning-500)' }}>
            {stats.expiringCritical > 0 ? <AlertCircle size={24} /> : <Clock size={24} />}
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.expiringCritical + stats.expiringSoon}</div>
            <div className="stat-label">Expiring Soon</div>
            <div className="stat-hint">
              {stats.expiringCritical > 0 && (
                <span className="stat-critical">{stats.expiringCritical} critical (≤7 days)</span>
              )}
              {stats.expiringSoon > 0 && (
                <span className="stat-soon">{stats.expiringSoon} within 30 days</span>
              )}
              {stats.expiringCritical === 0 && stats.expiringSoon === 0 && (
                <span>None in next 30 days</span>
              )}
            </div>
          </div>
        </div>

        {stats.expired > 0 && (
          <div className="stat-card stat-card-danger">
            <div className="stat-icon" style={{ backgroundColor: 'var(--color-danger-600)' }}>
              <AlertCircle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.expired}</div>
              <div className="stat-label">Expired</div>
              <div className="stat-hint">Requires attention</div>
            </div>
          </div>
        )}

        <Link to="/audit" className="stat-card stat-card-link">
          <div className="stat-icon" style={{ backgroundColor: 'var(--color-success-500)' }}>
            <FileText size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.recentActivity}</div>
            <div className="stat-label">Recent Activity</div>
          </div>
          <ArrowUpRight size={16} className="stat-card-arrow" />
        </Link>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div className="card-header">
            <h2>Recent Activity</h2>
            <Link to="/audit" className="view-all-link">
              View All
            </Link>
          </div>
          <div className="activity-list">
            {recentLogs.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><FileText size={26} /></div>
                <p>No recent activity</p>
                <span>Actions taken across the vault will show up here</span>
              </div>
            ) : (
              recentLogs.slice(0, 5).map((log: any) => (
                <div key={log.id} className="activity-item">
                  <div className="activity-dot" />
                  <div className="activity-info">
                    <div className="activity-action">{log.action}</div>
                    <div className="activity-user">{log.user?.username}</div>
                  </div>
                  <div className="activity-time">
                    {new Date(log.createdAt).toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h2>Credential Health</h2>
          </div>
          {credentials.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><ShieldCheck size={26} /></div>
              <p>Nothing to show yet</p>
              <span>Add a credential to see its expiry health here</span>
            </div>
          ) : (
            <div className="health-chart">
              <svg viewBox="0 0 100 100" className="donut">
                <circle cx="50" cy="50" r="42" fill="none" stroke="var(--color-slate-100)" strokeWidth="12" />
                {segments.map((seg, i) => {
                  const fraction = seg.value / donutTotal;
                  const dash = fraction * circumference;
                  const el = (
                    <circle
                      key={i}
                      cx="50"
                      cy="50"
                      r="42"
                      fill="none"
                      stroke={seg.color}
                      strokeWidth="12"
                      strokeDasharray={`${dash} ${circumference - dash}`}
                      strokeDashoffset={-offsetAcc}
                      strokeLinecap="round"
                      transform="rotate(-90 50 50)"
                    />
                  );
                  offsetAcc += dash;
                  return el;
                })}
                <text x="50" y="47" textAnchor="middle" className="donut-value">{credentials.length}</text>
                <text x="50" y="62" textAnchor="middle" className="donut-label">total</text>
              </svg>
              <div className="health-legend">
                {segments.map((seg) => (
                  <div className="health-legend-item" key={seg.label}>
                    <span className="health-dot" style={{ backgroundColor: seg.color }} />
                    <span className="health-legend-label">{seg.label}</span>
                    <span className="health-legend-value">{seg.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h2>Quick Actions</h2>
          </div>
          <div className="quick-actions">
            <Link to="/credentials" className="action-button">
              <Key size={20} />
              <span>View All Credentials</span>
            </Link>
            <Link to="/credentials?new=true" className="action-button">
              <Plus size={20} />
              <span>Add New Credential</span>
            </Link>
            <Link to="/audit" className="action-button">
              <TrendingUp size={20} />
              <span>View Audit Logs</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
